import { Organization } from './Organization';

/**
 * 项目状态枚举
 */
export enum ProjectStatus {
  /** 草稿/编辑中 */
  DRAFT = 'DRAFT',
  /** 已发布/筹款中 */
  PUBLISHED = 'PUBLISHED',
  /** 已停止募捐 */
  STOPPED = 'STOPPED',
  /** 已下架/停止筹款 */
  OFFLINE = 'OFFLINE',
}

/**
 * 项目分类枚举
 */
export enum ProjectCategory {
  /** 乡村振兴 */
  RURAL_REVITALIZATION = 'RURAL_REVITALIZATION',
  /** 灾救援 */
  DISASTER_RELIEF = 'DISASTER_RELIEF',
  /** 教育助学 */
  EDUCATION_AID = 'EDUCATION_AID',
  /** 医疗卫生 */
  MEDICAL_HEALTH = 'MEDICAL_HEALTH',
  /** 疾病救助 */
  DISEASE_RELIEF = 'DISEASE_RELIEF',
  /** 助残助困 */
  DISABILITY_AID = 'DISABILITY_AID',
  /** 国际救助 */
  INTERNATIONAL_AID = 'INTERNATIONAL_AID',
  /** 生态环保 */
  ECOLOGICAL_PROTECTION = 'ECOLOGICAL_PROTECTION',
  /** 文体艺术 */
  CULTURE_SPORTS = 'CULTURE_SPORTS',
  /** 志愿服务 */
  VOLUNTEER_SERVICE = 'VOLUNTEER_SERVICE',
  /** 社区服务 */
  COMMUNITY_SERVICE = 'COMMUNITY_SERVICE',
  /** 其他 */
  OTHER = 'OTHER'
}

/**
 * 筹款类型枚举
 */
export enum FundraisingType {
  /** 单次捐 */
  ONE_TIME = 'ONE_TIME',
  /** 仅月捐 */
  MONTHLY_ONLY = 'MONTHLY_ONLY',
  /** 月捐+单次捐 */
  MONTHLY_AND_ONE_TIME = 'MONTHLY_AND_ONE_TIME'
}

/**
 * 审核类型枚举
 */
export enum ReviewType {
  /** 新发布 */
  NEW_RELEASE = 'NEW_RELEASE',
  /** 更新内容 */
  CONTENT_UPDATE = 'CONTENT_UPDATE'
}

/**
 * 筹款设置项
 */
export interface FundraisingTier {
  /** 金额（元） */
  amount: number;
  /** 描述 */
  description: string;
}

/**
 * 项目数据模型
 */
export interface Project {
  /** 唯一标识 */
  id: string;
  
  /** 项目标题 */
  title: string;
  
  /** 项目简介（46字以内） */
  description: string;
  
  /** 项目封面图 URL（兼容旧字段，大图 16:9） */
  coverImageUrl?: string;
  /** 项目大图（16:9，用于详情） */
  bigImageUrl?: string;
  /** 项目缩略图（1:1，用于列表） */
  coverThumbUrl?: string;
  
  /** 项目状态 */
  status: ProjectStatus;
  
  /** 所属机构 ID */
  organizationId: string;
  
  /** 完整的公募机构信息 */
  organization?: Organization;
  
  /** 执行机构 ID（公募机构创建项目时需要选择执行机构） */
  executingOrganizationId?: string;
  
  /** 完整的执行机构信息 */
  executingOrganization?: Organization;
  
  /** 公开募捐编号（24位备案编号） */
  publicFundraisingNumber?: string;
  
  /** 备案号截止时间 */
  filingNumberExpiryDate?: string;
  
  /** 项目分类 */
  category?: ProjectCategory;
  
  /** 目标金额（元），0 表示不设目标 */
  targetAmount: number;
  
  /** 已筹金额（元） */
  raisedAmount: number;
  
  /** 参与人数/捐赠人次 */
  participantCount: number;
  
  /** 捐赠人数 */
  donorCount?: number;
  
  /** 项目详情（富文本） */
  detail?: string;
  
  /** 项目口号 */
  slogan?: string;
  
  /** 项目负责人 */
  projectManager?: string;
  
  /** 联系电话 */
  contactPhone?: string;
  
  /** 筹款类型 */
  fundraisingType?: FundraisingType;
  
  /** 是否开启微信支付签约（月捐） */
  wechatPaySigningEnabled?: boolean;
  
  /** 微信委托代扣模板 ID */
  wechatDeductionTemplateId?: string;
  
  /** 筹款设置（分档） */
  fundraisingTiers?: FundraisingTier[];
  
  /** 是否支持自定义金额 */
  supportCustomAmount?: boolean;
  
  /** 自定义金额下限（元），0 表示不限制 */
  customAmountMin?: number;
  
  /** 自定义金额上限（元），0 表示不限制 */
  customAmountMax?: number;
  
  /** 是否置顶 */
  isPinned?: boolean;
  
  /** 置顶时间 */
  pinnedTime?: string;
  
  /** 审核类型 */
  reviewType?: ReviewType;
  
  /** 审核意见（审核拒绝时） */
  reviewComment?: string;
  
  /** 审核时间 */
  reviewTime?: string;
  
  /** 审核人 ID */
  reviewerId?: string;
  
  /** 提审时间（提交审核的时间） */
  submitReviewTime?: string;
  
  /** 创建时间 */
  createTime: string;
  
  /** 更新时间 */
  updateTime: string;
  
  /** 发布时间 */
  publishTime?: string;
}

